/**
 * Chutt... - Simple In-Memory Cache Service
 * Caches API responses to avoid redundant calls when navigating between pages
 */

// Cache storage
const cache = new Map();

// Default TTL: 5 minutes
const DEFAULT_TTL = 5 * 60 * 1000;

/**
 * Generate cache key from endpoint and params
 */
function generateKey(endpoint, params = {}) {
  const paramStr = Object.keys(params)
    .sort()
    .map(k => `${k}=${params[k]}`)
    .join('&');
  return `${endpoint}?${paramStr}`;
}

/**
 * Get cached data if valid
 */
export function getCached(endpoint, params = {}) {
  const key = generateKey(endpoint, params);
  const entry = cache.get(key);

  if (!entry) return null;

  // Check if expired
  if (Date.now() > entry.expiresAt) {
    cache.delete(key);
    return null;
  }

  return entry.data;
}

/**
 * Set cache entry
 */
export function setCache(endpoint, params = {}, data, ttl = DEFAULT_TTL) {
  const key = generateKey(endpoint, params);
  cache.set(key, {
    data,
    expiresAt: Date.now() + ttl,
    cachedAt: Date.now(),
  });
}

/**
 * Invalidate cache for a specific endpoint pattern
 */
export function invalidateCache(pattern = null) {
  if (!pattern) {
    cache.clear();
    return;
  }

  for (const key of cache.keys()) {
    if (key.includes(pattern)) {
      cache.delete(key);
    }
  }
}

/**
 * Invalidate all cache for a specific brand
 */
export function invalidateBrandCache(brandId) {
  for (const key of cache.keys()) {
    if (key.includes(`brand_id=${brandId}`) || key.includes(`/${brandId}/`) || key.includes(`/${brandId}?`)) {
      cache.delete(key);
    }
  }
}

/**
 * Get cache stats (for debugging)
 */
export function getCacheStats() {
  const stats = {
    entries: cache.size,
    keys: Array.from(cache.keys()),
  };
  return stats;
}

/**
 * Wrapper for fetch with cache - use this in API calls
 */
export async function cachedFetch(fetchFn, endpoint, params = {}, options = {}) {
  const { ttl = DEFAULT_TTL, forceRefresh = false } = options;

  // Check cache first (unless force refresh)
  if (!forceRefresh) {
    const cached = getCached(endpoint, params);
    if (cached) {
      return { data: cached, fromCache: true };
    }
  }

  // Fetch fresh data
  const response = await fetchFn();

  // Cache the result
  if (response?.data) {
    setCache(endpoint, params, response.data, ttl);
  }

  return { ...response, fromCache: false };
}

const cacheService = {
  getCached,
  setCache,
  invalidateCache,
  invalidateBrandCache,
  getCacheStats,
  cachedFetch,
};

export default cacheService;
